/**
 * Buttons.
 *
 * Four variants and three heights — 28 / 34 / 40 — and nothing else. The heights
 * are fixed rather than derived from padding so a button always lines up with the
 * input, select, and chip sitting next to it in a filter bar.
 *
 * `loading` is a disabled state, not a decoration: the control stops accepting
 * clicks and reports `aria-busy` so a screen reader is told the action is in
 * flight instead of silently doing nothing.
 */

import React from 'react';
import { Spinner } from './Skeleton';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* Hover/active are held separately from the resting look so a disabled or
   loading control can drop them entirely. `:disabled` styling cannot do this
   job: `as="a"` renders an anchor, and `:disabled` never matches one. */
const VARIANT = {
  primary: {
    base: 'border border-transparent bg-brand text-brand-fg',
    interactive: 'hover:brightness-95 active:brightness-90',
  },
  secondary: {
    base: 'border border-line bg-surface text-ink-900',
    interactive: 'hover:bg-surface-2 active:bg-surface-3',
  },
  ghost: {
    base: 'border border-transparent bg-transparent text-ink-700',
    interactive: 'hover:bg-surface-2 hover:text-ink-900 active:bg-surface-3',
  },
  danger: {
    // `bad-fill` is the same red in both themes, so its foreground must be too.
    // `brand-fg` inverts with the theme and would put near-black on red.
    base: 'border border-transparent bg-bad-fill text-white',
    interactive: 'hover:brightness-95 active:brightness-90',
  },
};

/* Padding and the square width are separate entries rather than a `px-0`
   override, because Tailwind emits padding utilities in its own order and a
   later class in the string does not win. */
const SIZE = {
  sm: { box: 'h-7 gap-1.5 text-micro', px: 'px-2.5', square: 'w-7' },
  md: { box: 'h-[34px] gap-2 text-label', px: 'px-3', square: 'w-[34px]' },
  lg: { box: 'h-10 gap-2 text-body', px: 'px-4', square: 'w-10' },
};

const SPINNER_PX = { sm: 12, md: 14, lg: 16 };

const BASE =
  'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap ' +
  'rounded-md font-medium no-underline transition-colors duration-fast';

/**
 * Accepts either a Font Awesome class name ('fa-plus', 'far fa-clock') or a
 * ready-made node, so callers are not forced to repeat the `<i aria-hidden>`.
 */
const renderIcon = (icon) => {
  if (icon === null || icon === undefined || icon === false) return null;
  if (typeof icon !== 'string') return icon;
  const styled = /(^|\s)(fas|far|fab|fal|fad|fa-solid|fa-regular|fa-brands|fa-light)(\s|$)/.test(icon);
  return <i className={styled ? icon : `fas ${icon}`} aria-hidden="true" />;
};

/**
 * @param {object}  props
 * @param {'primary'|'secondary'|'ghost'|'danger'} [props.variant]
 * @param {'sm'|'md'|'lg'} [props.size]
 * @param {boolean} [props.loading]   spinner + aria-busy + inert
 * @param {*}       [props.iconLeft]  FA class string or node
 * @param {*}       [props.iconRight]
 * @param {string}  [props.as]        element/component override; `href` implies 'a'
 * @param {boolean} [props.square]    icon-only footprint; set by IconButton
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  as,
  href,
  square = false,
  className = '',
  onClick,
  children,
  ...rest
}) {
  const tone = VARIANT[variant] || VARIANT.secondary;
  const sizing = SIZE[size] || SIZE.md;
  const inert = Boolean(disabled) || Boolean(loading);

  const Component = as || (href !== undefined ? 'a' : 'button');
  const isNativeButton = Component === 'button';

  // Guards every element type, including a custom `as` (a router Link keeps its
  // `to` prop, so removing `href` is not enough to make it inert).
  const handleClick = (event) => {
    if (inert) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (onClick) onClick(event);
  };

  const props = {
    ...rest,
    onClick: handleClick,
    className: cx(
      BASE,
      sizing.box,
      square ? sizing.square : sizing.px,
      tone.base,
      inert ? (loading ? 'cursor-wait opacity-70' : 'cursor-not-allowed opacity-50') : tone.interactive,
      fullWidth && 'w-full',
      className,
    ),
  };

  if (isNativeButton) {
    // Never let a button inside a form submit it by accident.
    props.type = rest.type || 'button';
    props.disabled = inert;
  } else {
    if (href !== undefined) props.href = inert ? undefined : href;
    if (inert) props['aria-disabled'] = true;
  }
  if (loading) props['aria-busy'] = true;

  const leading = loading ? (
    <span className="inline-flex shrink-0" aria-hidden="true">
      <Spinner size={SPINNER_PX[size] || SPINNER_PX.md} />
    </span>
  ) : (
    renderIcon(iconLeft)
  );

  return (
    <Component {...props}>
      {leading}
      {children}
      {!loading && renderIcon(iconRight)}
    </Component>
  );
}

/**
 * Icon-only control. `label` is required — it is the only accessible name the
 * control has, and it doubles as the hover title.
 *
 * The icon travels as `iconLeft` rather than as children so `loading` swaps it
 * for the spinner instead of showing both.
 */
export function IconButton({ icon, label, variant = 'ghost', size = 'md', className = '', ...rest }) {
  return (
    <Button
      variant={variant}
      size={size}
      square
      iconLeft={icon}
      aria-label={label}
      title={label}
      className={className}
      {...rest}
    />
  );
}
